/**
 * TimeChart: one SVG chart for everything date-based (weight trend, calorie
 * bars, projections with an uncertainty band). Responsive, with hover tooltips.
 */
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { formatDayShort } from '../lib/dates';

export interface LineSeries { name: string; color: string; points: ReadonlyArray<{ day: string; y: number }>; dashed?: boolean; dots?: boolean; width?: number; hideLine?: boolean }
export interface BarSeries { name: string; points: ReadonlyArray<{ day: string; y: number; color?: string }>; color: string }
export interface BandSeries { color: string; points: ReadonlyArray<{ day: string; lo: number; hi: number }> }
export interface RefLine { y: number; label: string; color: string }

interface Props {
  lines?: LineSeries[];
  bars?: BarSeries;
  band?: BandSeries;
  refLines?: RefLine[];
  height?: number;
  yFormat?: (v: number) => string;
  /** Force y-axis to include zero (bar charts). */
  zeroBased?: boolean;
  emptyText?: string;
}

const dayNum = (d: string): number => Math.round(Date.parse(`${d}T00:00:00Z`) / 86_400_000);
const numDay = (n: number): string => new Date(n * 86_400_000).toISOString().slice(0, 10);

function niceTicks(min: number, max: number, count: number): number[] {
  if (max <= min) return [min];
  const raw: number = (max - min) / count;
  const mag: number = 10 ** Math.floor(Math.log10(raw));
  const step: number = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(Math.round(v * 1000) / 1000);
  return out;
}

export function TimeChart({ lines = [], bars, band, refLines = [], height = 240, yFormat = (v) => v.toFixed(0), zeroBased = false, emptyText = 'No data yet' }: Props): ReactElement {
  const boxRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el: HTMLDivElement | null = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w: number | undefined = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const geo = useMemo(() => {
    const xs: number[] = [];
    const ys: number[] = [];
    for (const l of lines) for (const p of l.points) { xs.push(dayNum(p.day)); ys.push(p.y); }
    if (bars) for (const p of bars.points) { xs.push(dayNum(p.day)); ys.push(p.y); }
    if (band) for (const p of band.points) { xs.push(dayNum(p.day)); ys.push(p.lo, p.hi); }
    for (const r of refLines) ys.push(r.y);
    if (xs.length === 0) return null;
    let x0: number = Math.min(...xs);
    let x1: number = Math.max(...xs);
    if (bars) { x0 -= 0.5; x1 += 0.5; }
    if (x1 === x0) { x0 -= 1; x1 += 1; }
    let y0: number = zeroBased ? 0 : Math.min(...ys);
    let y1: number = Math.max(...ys);
    const pad: number = (y1 - y0 || Math.abs(y1) || 1) * 0.08;
    if (!zeroBased) y0 -= pad;
    y1 += pad;
    const m = { l: 48, r: 14, t: 12, b: 28 };
    const w: number = Math.max(200, width);
    const sx = (x: number): number => m.l + ((x - x0) / (x1 - x0)) * (w - m.l - m.r);
    const sy = (y: number): number => m.t + (1 - (y - y0) / (y1 - y0)) * (height - m.t - m.b);
    return { x0, x1, y0, y1, m, w, sx, sy };
  }, [lines, bars, band, refLines, width, height, zeroBased]);

  if (!geo) {
    return <div ref={boxRef} className="empty" style={{ height, display: 'grid', placeItems: 'center' }}>{emptyText}</div>;
  }
  const { x0, x1, y0, y1, m, w, sx, sy } = geo;
  const yTicks: number[] = niceTicks(y0, y1, 4);
  const spanDays: number = x1 - x0;
  const xStep: number = Math.max(1, Math.ceil(spanDays / Math.max(2, Math.floor((w - m.l - m.r) / 80))));
  const xTicks: number[] = [];
  for (let x = Math.ceil(x0); x <= x1; x += xStep) xTicks.push(x);
  const barW: number = bars ? Math.max(2, Math.min(28, ((w - m.l - m.r) / Math.max(1, spanDays)) * 0.62)) : 0;

  const path = (pts: ReadonlyArray<{ day: string; y: number }>): string =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(dayNum(p.day)).toFixed(1)},${sy(p.y).toFixed(1)}`).join('');

  const bandPath: string | null = band && band.points.length > 1
    ? `${band.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(dayNum(p.day))},${sy(p.hi)}`).join('')}${[...band.points].reverse().map((p) => `L${sx(dayNum(p.day))},${sy(p.lo)}`).join('')}Z`
    : null;

  const onMove = (e: React.MouseEvent<SVGRectElement>): void => {
    const rect: DOMRect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    const px: number = e.clientX - rect.left;
    const x: number = x0 + ((px - m.l) / (w - m.l - m.r)) * (x1 - x0);
    setHover(Math.round(x));
  };

  const tipRows: Array<{ name: string; color: string; value: string }> = [];
  if (hover !== null) {
    const hd: string = numDay(hover);
    if (bars) { const b = bars.points.find((p) => p.day === hd); if (b) tipRows.push({ name: bars.name, color: b.color ?? bars.color, value: yFormat(b.y) }); }
    for (const l of lines) { const p = l.points.find((q) => q.day === hd); if (p) tipRows.push({ name: l.name, color: l.color, value: yFormat(p.y) }); }
  }
  const hoverX: number | null = hover !== null ? sx(hover) : null;

  return (
    <div ref={boxRef} className="chart-box">
      <svg className="chart" width={w} height={height} viewBox={`0 0 ${w} ${height}`}>
        {yTicks.map((t) => (
          <g key={t}>
            <line className="grid-line" x1={m.l} x2={w - m.r} y1={sy(t)} y2={sy(t)} />
            <text x={m.l - 8} y={sy(t) + 4} textAnchor="end">{yFormat(t)}</text>
          </g>
        ))}
        {xTicks.map((t) => <text key={t} x={sx(t)} y={height - 8} textAnchor="middle">{formatDayShort(numDay(t))}</text>)}
        {bandPath !== null && band && <path d={bandPath} fill={band.color} opacity={0.18} />}
        {bars?.points.map((p) => (
          <rect key={p.day} x={sx(dayNum(p.day)) - barW / 2} y={sy(Math.max(p.y, 0))} width={barW} height={Math.max(0, sy(0) - sy(Math.max(p.y, 0)))} rx={Math.min(4, barW / 3)} fill={p.color ?? bars.color} opacity={hover !== null && numDay(hover) !== p.day ? 0.55 : 1} />
        ))}
        {refLines.map((r, i) => (
          <g key={r.label}>
            <line x1={m.l} x2={w - m.r} y1={sy(r.y)} y2={sy(r.y)} stroke={r.color} strokeWidth={1.5} strokeDasharray="5 4" />
            {/* Alternate label sides so two nearby lines don't print on top of each other. */}
            <text x={i % 2 === 0 ? w - m.r : m.l + 6} y={sy(r.y) - 5} textAnchor={i % 2 === 0 ? 'end' : 'start'} style={{ fill: r.color, paintOrder: 'stroke', stroke: 'var(--surface)', strokeWidth: 3 }}>{r.label}</text>
          </g>
        ))}
        {lines.map((l) => (
          <g key={l.name}>
            {!l.hideLine && l.points.length > 1 && <path d={path(l.points)} fill="none" stroke={l.color} strokeWidth={l.width ?? 2.5} strokeDasharray={l.dashed ? '6 5' : undefined} strokeLinejoin="round" strokeLinecap="round" />}
            {l.dots && l.points.map((p) => <circle key={p.day} cx={sx(dayNum(p.day))} cy={sy(p.y)} r={3} fill={l.color} opacity={0.55} />)}
          </g>
        ))}
        {hoverX !== null && tipRows.length > 0 && <line x1={hoverX} x2={hoverX} y1={m.t} y2={height - m.b} stroke="var(--text-3)" strokeDasharray="3 3" />}
        <rect x={m.l} y={m.t} width={Math.max(0, w - m.l - m.r)} height={Math.max(0, height - m.t - m.b)} fill="transparent" onMouseMove={onMove} onMouseLeave={() => setHover(null)} />
      </svg>
      {hoverX !== null && tipRows.length > 0 && hover !== null && (
        <div className="chart-tip" style={{ left: Math.min(Math.max(hoverX, 70), w - 70), top: m.t + 8 }}>
          <div style={{ opacity: 0.7, marginBottom: 2 }}>{formatDayShort(numDay(hover))}</div>
          {tipRows.map((r) => (
            <div key={r.name} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span className="dot" style={{ background: r.color }} /> {r.name}: {r.value}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
